import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import config from "../../../config";
import TextToggle from "../../../components/toggle";
import TableBodyLoading from "../../../components/tableLoading";
import {
  FaChevronDown,
  FaChevronUp,
  FaFilter,
  FaTimes,
  FaLayerGroup,
  FaSyncAlt,
  FaDownload,
} from "react-icons/fa";
import DealerShopInsights from "../ExtractionReport/DealerShopInsights";
import "./style.scss";

const backend_url = config.backend_url;
const FLOW_NAME = "default_sales_flow";

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const DEALER_FILTER_TYPES = [
  { key: "zone", label: "Zone" },
  { key: "district", label: "District" },
  { key: "town", label: "Town" },
  { key: "category", label: "Category" },
  { key: "labels", label: "Manned / Unmanned" },
  { key: "product_tags", label: "Product Tags" },
  { key: "top_outlet", label: "Top Outlet" },
];

const HEATMAP_STOPS = [
  [248, 96, 103],
  [250, 124, 109],
  [255, 176, 121],
  [255, 218, 130],
  [255, 241, 132],
  [195, 223, 130],
  [132, 199, 124],
  [96, 189, 125],
];

const getRedToGreenHeatmapColor = (normalized) => {
  const clamped = Math.max(0, Math.min(1, normalized));
  const scaled = clamped * (HEATMAP_STOPS.length - 1);
  const index = Math.min(Math.floor(scaled), HEATMAP_STOPS.length - 2);
  const t = scaled - index;
  const [r1, g1, b1] = HEATMAP_STOPS[index];
  const [r2, g2, b2] = HEATMAP_STOPS[index + 1];
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return {
    background: `rgb(${r}, ${g}, ${b})`,
    text: luminance < 0.58 ? "#ffffff" : "#1f2937",
  };
};

const parseNumericValue = (value) => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return NaN;

  const normalized = value.replace(/−/g, "-");
  const numericString = normalized.replace(/[^0-9.-]/g, "");
  const num = parseFloat(numericString);
  return isNaN(num) ? NaN : num;
};

const getHeatmapStats = (row, header) => {
  const values = header
    .filter((key) => !["Price Class", "Group", "Rank of Samsung", "Total"].includes(key))
    .map((key) => parseNumericValue(row[key]))
    .filter((v) => !isNaN(v));

  if (!values.length) {
    return {
      min: 0,
      max: 0,
      p25: 0,
      p50: 0,
      p75: 0,
    };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const getPercentile = (arr, p) => {
    const index = (arr.length - 1) * p;
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    if (lower === upper) return arr[lower];
    return arr[lower] + (arr[upper] - arr[lower]) * (index - lower);
  };

  return {
    min: sorted[0],
    max: sorted[sorted.length - 1],
    p25: getPercentile(sorted, 0.25),
    p50: getPercentile(sorted, 0.5),
    p75: getPercentile(sorted, 0.75),
  };
};

const getSmartHeatmapColor = (value, stats) => {
  const numValue = parseNumericValue(value);
  if (isNaN(numValue)) return { background: "", text: "" };

  const { min, max } = stats;

  if (min === max) {
    return {
      background: "rgb(248,250,252)",
      text: "#374151",
    };
  }

  const spreadRatio = max / Math.max(min || 1, 1);
  let normalized = 0;

  if (spreadRatio > 8) {
    const safeMin = Math.max(0, min);
    const shifted = numValue - safeMin + 1;
    const shiftedMax = max - safeMin + 1;
    normalized =
      shiftedMax <= 1 ? 0 : Math.log(shifted) / Math.log(shiftedMax);
  } else {
    normalized = (numValue - min) / (max - min);
  }

  // clamp
  normalized = Math.max(0, Math.min(1, normalized));

  return getRedToGreenHeatmapColor(normalized);
};

const formatNormalNumber = (value) => {
  if (typeof value !== "string" && typeof value !== "number") return value;
  if (typeof value === "string" && value.includes("%")) return value;

  const num = parseNumericValue(value);
  if (isNaN(num)) return value;

  return num.toLocaleString("en-IN", {
    useGrouping: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatCrLacNumber = (value) => {
  if (typeof value !== "string" && typeof value !== "number") return value;
  if (typeof value === "string" && value.includes("%")) return value;

  const num = parseNumericValue(value);
  if (isNaN(num)) return value;

  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  if (abs >= 10000000) {
    return `${sign}${(abs / 10000000).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} Cr`;
  }

  if (abs >= 100000) {
    return `${sign}${(abs / 100000).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} Lac`;
  }

  if (abs >= 1000) {
    return `${sign}${(abs / 1000).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} K`;
  }

  return `${sign}${abs.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const getRedShadeColor = (value, rowMin, rowMax) => {
  const numValue = parseNumericValue(value);
  if (isNaN(numValue) || rowMin === rowMax) return { background: "", text: "" };

  const normalized = (numValue - rowMin) / (rowMax - rowMin);

  return getRedToGreenHeatmapColor(normalized);
};

const calculateRowHeatmapRange = (row, header) => {
  let min = Infinity;
  let max = -Infinity;

  header.forEach((headerKey) => {
    if (!["Price Class", "Group", "Rank of Samsung", "Total"].includes(headerKey)) {
      const numValue = parseNumericValue(row[headerKey]);
      if (!isNaN(numValue)) {
        min = Math.min(min, numValue);
        max = Math.max(max, numValue);
      }
    }
  });

  return { min, max };
};

const getDefaultPrevMonthRange = () => {
  const now = new Date();
  const firstDayPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastDayPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
  const toInputDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  return {
    start: toInputDate(firstDayPrevMonth),
    end: toInputDate(lastDayPrevMonth),
  };
};

const decodeJwtPayload = (token) => {
  try {
    if (!token || typeof token !== "string") return {};
    const parts = token.split(".");
    if (parts.length < 2) return {};
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = atob(padded);
    return JSON.parse(json) || {};
  } catch (_) {
    return {};
  }
};

function DynamicExtractionReport() {
  const prevMonthRange = useMemo(() => getDefaultPrevMonthRange(), []);
  const defaultDownloadParts = useMemo(() => {
    const [year, month] = prevMonthRange.start.split("-").map(Number);
    return { month, year };
  }, [prevMonthRange.start]);

  const [startDate, setStartDate] = useState(prevMonthRange.start);
  const [endDate, setEndDate] = useState(prevMonthRange.end);
  const [downloadMonth, setDownloadMonth] = useState(defaultDownloadParts.month);
  const [downloadYear, setDownloadYear] = useState(defaultDownloadParts.year);
  const [isDownloadingCombinedReport, setIsDownloadingCombinedReport] = useState(false);
  const [metric, setMetric] = useState("volume");
  const [valueFormat, setValueFormat] = useState("crLac");
  const [view, setView] = useState("default");

  const [groupingOptions, setGroupingOptions] = useState([]);
  const [actorPositions, setActorPositions] = useState([]);

  const [groupBy, setGroupBy] = useState("price_segment");
  const [groupPosition, setGroupPosition] = useState("");

  const [filterValues, setFilterValues] = useState({
    zone: [],
    district: [],
    town: [],
    category: [],
    labels: [],
    product_tags: [],
    top_outlet: [],
  });

  const [actorOptionsMap, setActorOptionsMap] = useState({});

  const [selectedActorFilters, setSelectedActorFilters] = useState({});
  const [selectedDealerFilters, setSelectedDealerFilters] = useState({
    zone: [],
    district: [],
    town: [],
    category: [],
    labels: [],
    product_tags: [],
    top_outlet: [],
  });

  const [brand, setBrand] = useState("");
  const [segment, setSegment] = useState("");

  const [reportRows, setReportRows] = useState([]);
  const [header, setHeader] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [scopedDealerCodes, setScopedDealerCodes] = useState([]);
  const [productDetail, setProductDetail] = useState({
    open: false,
    loading: false,
    title: "",
    columns: [],
    rows: [],
    error: "",
  });

  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [activeFilterTab, setActiveFilterTab] = useState("zone");
  const [searchText, setSearchText] = useState("");

  const [groupActorOptions, setGroupActorOptions] = useState([]);
  const [groupActorSelection, setGroupActorSelection] = useState([]);

  const panelRef = useRef(null);

  const authHeaders = useMemo(
    () => ({
      Authorization: localStorage.getItem("authToken"),
    }),
    []
  );

  const authContext = useMemo(() => {
    const token = localStorage.getItem("authToken");
    const decoded = decodeJwtPayload(token);
    const role = String(localStorage.getItem("role") || decoded?.role || "")
      .trim()
      .toLowerCase();
    const position = String(decoded?.position || "")
      .trim()
      .toLowerCase();
    return { role, position };
  }, []);

  const isAdminLikeUser = useMemo(
    () => ["admin", "super_admin"].includes(authContext.role),
    [authContext.role]
  );

  const downloadYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, index) => currentYear - 3 + index);
  }, []);

  const actorPositionItems = useMemo(() => {
    if (actorPositions?.length) return actorPositions;
    return ACTOR_POSITION_KEYS.map((value) => ({
      value,
      label: String(value).toUpperCase(),
    }));
  }, [actorPositions]);

  const visibleActorPositionItems = useMemo(() => {
    if (isAdminLikeUser) return actorPositionItems;
    const myPos = authContext.position;
    if (!myPos) return actorPositionItems;

    const idx = actorPositionItems.findIndex(
      (item) => String(item.value || "").toLowerCase() === myPos
    );

    if (idx < 0) return actorPositionItems;
    return actorPositionItems.slice(idx + 1);
  }, [actorPositionItems, isAdminLikeUser, authContext.position]);

  const actorPositionOrder = useMemo(
    () => visibleActorPositionItems.map((item) => item.value).filter(Boolean),
    [visibleActorPositionItems]
  );

  const isActorTab = (tabKey) =>
    actorPositionOrder.includes(tabKey);

  const fetchGroupingOptions = async () => {
    try {
      const res = await axios.get(`${backend_url}/grouping-options`, {
        headers: authHeaders,
      });

      setGroupingOptions(res.data.groupByOptions || []);
      setActorPositions(res.data.actorPositions || []);

      if (!groupBy && res.data.groupByOptions?.length) {
        setGroupBy(res.data.groupByOptions[0].value);
      }
    } catch (error) {
      console.error("Error fetching grouping options:", error);
    }
  };

  const buildSubordinateFilters = (
    source = selectedActorFilters,
    { upToPosition = null } = {}
  ) => {
    const filters = {};
    const orderedPositions = actorPositionOrder.length
      ? actorPositionOrder
      : ACTOR_POSITION_KEYS;

    for (const position of orderedPositions) {
      if (upToPosition && position === upToPosition) break;

      const codes = (source[position] || [])
        .map((item) => item.code)
        .filter(Boolean);

      if (codes.length) {
        filters[position] = codes;
      }
    }

    return filters;
  };

  const buildDealerFiltersPayload = (
    source = selectedDealerFilters,
    { excludeType = null } = {}
  ) => {
    const filters = {};

    Object.entries(source).forEach(([key, selected]) => {
      if (key === excludeType || !selected?.length) return;

      const values = selected
        .map((item) => item.value)
        .filter((value) => value !== undefined && value !== null && value !== "");

      if (values.length) {
        filters[key] = values;
      }
    });

    return filters;
  };

  const fetchDropdownOptions = async ({
    targetType,
    targetKey,
    subordinates = {},
    dealer = {},
    productTags = {},
  }) => {
    try {
      const body = {
        flow_name: FLOW_NAME,
        target_type: targetType,
        target_key: targetKey,
        subordinates,
        dealer,
        product_tags: productTags,
      };

      const res = await axios.post(`${backend_url}/filters/dropdown-options`, body, {
        headers: authHeaders,
      });

      return res.data?.values || [];
    } catch (error) {
      console.error(
        `Error fetching dropdown options for ${targetType}/${targetKey}:`,
        error
      );
      return [];
    }
  };

  const loadFilterOptionsForTab = async (tabKey) => {
    if (!tabKey) return;

    if (tabKey === "labels") {
      setFilterValues((old) => ({
        ...old,
        labels: [
          { label: "Manned", value: "manned" },
          { label: "Unmanned", value: "unmanned" },
        ],
      }));
      return;
    }

    if (tabKey === "product_tags") {
      const selectedTags = (selectedDealerFilters.product_tags || [])
        .map((item) => item.value)
        .filter(Boolean);

      const values = await fetchDropdownOptions({
        targetType: "product_tag",
        targetKey: "product_tag",
        subordinates: {},
        dealer: {},
        productTags: selectedTags.length ? { product_tag: selectedTags } : {},
      });

      setFilterValues((old) => ({
        ...old,
        [tabKey]: values,
      }));
      return;
    }

    if (isActorTab(tabKey)) {
      const values = await fetchDropdownOptions({
        targetType: "subordinate",
        targetKey: tabKey,
        subordinates: buildSubordinateFilters(selectedActorFilters, {
          upToPosition: tabKey,
        }),
        dealer: buildDealerFiltersPayload(),
      });

      setActorOptionsMap((old) => ({
        ...old,
        [tabKey]: values,
      }));
      return;
    }

    const values = await fetchDropdownOptions({
      targetType: "dealer",
      targetKey: tabKey,
      subordinates: buildSubordinateFilters(),
      dealer: buildDealerFiltersPayload(selectedDealerFilters, {
        excludeType: tabKey,
      }),
    });

    setFilterValues((old) => ({
      ...old,
      [tabKey]: values,
    }));
  };

  const loadGroupActorOptions = async (positionValue) => {
    if (!positionValue) {
      setGroupActorOptions([]);
      return;
    }

    if (actorOptionsMap[positionValue]?.length) {
      setGroupActorOptions(actorOptionsMap[positionValue]);
      return;
    }

    const values = await fetchDropdownOptions({
      targetType: "subordinate",
      targetKey: positionValue,
      subordinates: buildSubordinateFilters(selectedActorFilters, {
        upToPosition: positionValue,
      }),
      dealer: buildDealerFiltersPayload(),
    });

    setActorOptionsMap((prev) => ({ ...prev, [positionValue]: values }));
    setGroupActorOptions(values);
  };

  const buildParams = () => {
    const params = {
      startDate,
      endDate,
      metric,
      view,
      groupBy,
    };

    if (groupBy === "actor" && groupPosition) {
      params.groupPosition = groupPosition;
    }

    if (brand) params.brand = brand;
    if (segment) params.segment = segment;

    Object.entries(selectedActorFilters).forEach(([position, selected]) => {
      if (selected?.length) {
        params[position] = selected.map((item) => item.code).join(",");
      }
    });

    Object.entries(selectedDealerFilters).forEach(([key, selected]) => {
      if (selected?.length) {
        if (key === "top_outlet") {
          params[key] = selected[0]?.value;
        } else {
          params[key] = selected.map((item) => item.value).join(",");
        }
      }
    });

    return params;
  };

  const getExtractionReport = async () => {
    try {
      setIsLoading(true);

      const res = await axios.get(`${backend_url}/dynamic-report`, {
        params: buildParams(),
        headers: authHeaders,
      });

      const rows = res.data.data || [];
      setReportRows(rows);
      setScopedDealerCodes(
        Array.isArray(res.data?.dealerFilter) ? res.data.dealerFilter : []
      );

      if (rows.length > 0) {
        const keys = Object.keys(rows[0]);
        const preferredOrder = [
          "Price Class",
          "Group",
          "Samsung",
          "Vivo",
          "Oppo",
          "Xiaomi",
          "Apple",
          "OnePlus",
          "Realme",
          "Motorola",
          "Others",
          "Rank of Samsung",
          "Total",
        ];

        const sortedHeaders = preferredOrder.filter((k) => keys.includes(k));
        setHeader(sortedHeaders);
      } else {
        setHeader(
          groupBy === "price_segment"
            ? [
                "Price Class",
                "Samsung",
                "Vivo",
                "Oppo",
                "Xiaomi",
                "Apple",
                "OnePlus",
                "Realme",
                "Motorola",
                "Others",
                "Rank of Samsung",
                "Total",
              ]
            : [
                "Group",
                "Samsung",
                "Vivo",
                "Oppo",
                "Xiaomi",
                "Apple",
                "OnePlus",
                "Realme",
                "Motorola",
                "Others",
                "Rank of Samsung",
                "Total",
              ]
        );
      }
    } catch (error) {
      console.error("Error fetching extraction report:", error);
      setReportRows([]);
      setScopedDealerCodes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const openProductDetail = async (row, brandColumn) => {
    const groupValue = row["Price Class"] || row.Group;
    if (!groupValue || !brandColumn) return;

    setProductDetail({
      open: true,
      loading: true,
      title: `${groupValue} · ${brandColumn}`,
      columns: [],
      rows: [],
      error: "",
    });

    try {
      const res = await axios.get(`${backend_url}/dynamic-report`, {
        params: {
          ...buildParams(),
          drilldownGroup: groupValue,
          drilldownBrand: brandColumn,
        },
        headers: authHeaders,
      });
      setProductDetail((current) => ({
        ...current,
        loading: false,
        columns: Array.isArray(res.data?.columns) ? res.data.columns : [],
        rows: Array.isArray(res.data?.rows) ? res.data.rows : [],
      }));
    } catch (error) {
      setProductDetail((current) => ({
        ...current,
        loading: false,
        error: error.response?.data?.message || "Failed to load product details",
      }));
    }
  };

  const downloadCombinedExtractionReport = async () => {
    if (!isAdminLikeUser) return;

    try {
      setIsDownloadingCombinedReport(true);

      const res = await fetch(
        `${backend_url}/download-combined-extraction-report-month-wise`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("authToken"),
          },
          body: JSON.stringify({
            month: Number(downloadMonth),
            year: Number(downloadYear),
          }),
        }
      );

      const contentType = res.headers.get("content-type") || "";

      if (!res.ok) {
        if (contentType.includes("application/json")) {
          const data = await res.json();
          window.alert(data.message || "Combined extraction report download failed");
        } else {
          window.alert("Combined extraction report download failed");
        }
        return;
      }

      if (
        !contentType.includes(
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        )
      ) {
        window.alert("Unexpected response while downloading combined extraction report");
        return;
      }

      const blob = await res.blob();
      const disposition = res.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const filename =
        filenameMatch?.[1] ||
        `extraction_full_report_${String(downloadMonth).padStart(2, "0")}_${downloadYear}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading combined extraction report:", error);
      window.alert("Network error while downloading combined extraction report");
    } finally {
      setIsDownloadingCombinedReport(false);
    }
  };

  useEffect(() => {
    fetchGroupingOptions();
  }, []);

  useEffect(() => {
    setStartDate(prevMonthRange.start);
    setEndDate(prevMonthRange.end);
  }, [prevMonthRange.start, prevMonthRange.end]);

  useEffect(() => {
    if (!filterPanelOpen || !activeFilterTab) return;
    loadFilterOptionsForTab(activeFilterTab);
  }, [
    filterPanelOpen,
    activeFilterTab,
    JSON.stringify(selectedActorFilters),
    JSON.stringify(selectedDealerFilters),
    JSON.stringify(actorPositionOrder),
  ]);

  useEffect(() => {
    if (!activeFilterTab && actorPositionOrder.length) {
      setActiveFilterTab(actorPositionOrder[0]);
    }
  }, [activeFilterTab, actorPositionOrder]);

  useEffect(() => {
    const validTabs = [
      ...actorPositionOrder,
      ...DEALER_FILTER_TYPES.map((d) => d.key),
    ];
    if (validTabs.length && !validTabs.includes(activeFilterTab)) {
      setActiveFilterTab(validTabs[0]);
    }
  }, [actorPositionOrder, activeFilterTab]);

  useEffect(() => {
    if (groupBy === "actor" && groupPosition) {
      loadGroupActorOptions(groupPosition);
    } else {
      setGroupActorOptions([]);
      setGroupActorSelection([]);
    }
  }, [groupBy, groupPosition]);

  useEffect(() => {
    if (
      groupBy === "actor" &&
      groupPosition &&
      !actorPositionOrder.includes(groupPosition)
    ) {
      setGroupPosition("");
      setGroupActorSelection([]);
    }
  }, [groupBy, groupPosition, actorPositionOrder]);

  useEffect(() => {
    getExtractionReport();
  }, [
    startDate,
    endDate,
    metric,
    view,
    groupBy,
    groupPosition,
    brand,
    segment,
    JSON.stringify(selectedActorFilters),
    JSON.stringify(selectedDealerFilters),
  ]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (filterPanelOpen && panelRef.current && !panelRef.current.contains(event.target)) {
        setFilterPanelOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [filterPanelOpen]);

  const resetAllFilters = () => {
    const prev = getDefaultPrevMonthRange();
    setStartDate(prev.start);
    setEndDate(prev.end);
    setMetric("volume");
    setView("default");
    setValueFormat("crLac");
    setGroupBy("price_segment");
    setGroupPosition("");
    setBrand("");
    setSegment("");
    setSelectedActorFilters({});
    setSelectedDealerFilters({
      zone: [],
      district: [],
      town: [],
      category: [],
      labels: [],
      product_tags: [],
      top_outlet: [],
    });
    setGroupActorSelection([]);
  };

  const totalSelectedFiltersCount = useMemo(() => {
    const actorCount = Object.values(selectedActorFilters).reduce(
      (sum, arr) => sum + (arr?.length || 0),
      0
    );
    const dealerCount = Object.values(selectedDealerFilters).reduce(
      (sum, arr) => sum + (arr?.length || 0),
      0
    );
    return actorCount + dealerCount;
  }, [selectedActorFilters, selectedDealerFilters]);

  const currentTabOptions = useMemo(() => {
    if (isActorTab(activeFilterTab)) {
      return actorOptionsMap[activeFilterTab] || [];
    }
    return filterValues[activeFilterTab] || [];
  }, [activeFilterTab, actorOptionsMap, filterValues, JSON.stringify(actorPositionOrder)]);

  const currentTabSelected = useMemo(() => {
    if (isActorTab(activeFilterTab)) {
      return selectedActorFilters[activeFilterTab] || [];
    }
    return selectedDealerFilters[activeFilterTab] || [];
  }, [
    activeFilterTab,
    selectedActorFilters,
    selectedDealerFilters,
    JSON.stringify(actorPositionOrder),
  ]);

  const filteredCurrentOptions = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return currentTabOptions;

    return currentTabOptions.filter((item) => {
      const raw =
        `${item.label || ""} ${item.name || ""} ${item.code || ""} ${item.value || ""}`.toLowerCase();
      return raw.includes(q);
    });
  }, [searchText, currentTabOptions]);

  const toggleSelection = (type, item) => {
    if (isActorTab(type)) {
      const orderedPositions = actorPositionOrder.length
        ? actorPositionOrder
        : ACTOR_POSITION_KEYS;
      const positionIndex = orderedPositions.indexOf(type);

      setSelectedActorFilters((old) => {
        const prev = old[type] || [];
        const exists = prev.some((x) => x.code === item.code);

        const next = {
          ...old,
          [type]: exists ? prev.filter((x) => x.code !== item.code) : [...prev, item],
        };

        if (positionIndex !== -1) {
          orderedPositions.slice(positionIndex + 1).forEach((position) => {
            next[position] = [];
          });
        }

        return next;
      });

      setActorOptionsMap((old) => {
        const next = { ...old };
        if (positionIndex !== -1) {
          orderedPositions.slice(positionIndex + 1).forEach((position) => {
            delete next[position];
          });
        }
        return next;
      });
      return;
    }

    const prev = selectedDealerFilters[type] || [];
    const identityKey = type === "top_outlet" ? "value" : "value";
    const exists = prev.some((x) => x[identityKey] === item[identityKey]);

    setSelectedDealerFilters((old) => ({
      ...old,
      [type]:
        type === "top_outlet"
          ? exists
            ? []
            : [item]
          : exists
          ? prev.filter((x) => x[identityKey] !== item[identityKey])
          : [...prev, item],
    }));
  };

  const removeSelection = (type, item) => {
    if (isActorTab(type)) {
      setSelectedActorFilters((old) => ({
        ...old,
        [type]: (old[type] || []).filter((x) => x.code !== item.code),
      }));
      return;
    }

    setSelectedDealerFilters((old) => ({
      ...old,
      [type]: (old[type] || []).filter((x) => x.value !== item.value),
    }));
  };

  const clearCurrentTab = () => {
    if (isActorTab(activeFilterTab)) {
      setSelectedActorFilters((old) => ({
        ...old,
        [activeFilterTab]: [],
      }));
      return;
    }

    setSelectedDealerFilters((old) => ({
      ...old,
      [activeFilterTab]: [],
    }));
  };

  const renderChips = (type) => {
    const selected = isActorTab(type)
      ? selectedActorFilters[type] || []
      : selectedDealerFilters[type] || [];

    if (!selected.length) return null;

    return (
      <div className="selected-chip-list">
        {selected.map((item) => (
          <button
            type="button"
            className="selected-chip"
            key={item.code || item.value}
            onClick={() => removeSelection(type, item)}
          >
            <span>{item.label || item.name || item.value}</span>
            <FaTimes />
          </button>
        ))}
      </div>
    );
  };

  const currentGroupLabel =
    groupingOptions.find((item) => item.value === groupBy)?.label || "Price Segment";

  const hierarchySelections = useMemo(() => {
    const rows = [];
    Object.entries(selectedActorFilters || {}).forEach(([position, selected]) => {
      (selected || []).forEach((item) => {
        if (!item?.code) return;
        rows.push({
          position: String(position || "").toLowerCase(),
          code: item.code,
          name: item.name || item.label || item.code,
        });
      });
    });
    return rows;
  }, [selectedActorFilters]);

  return (
    <div className="extraction-report-dynamic-page">
      <div className="page-topbar">
        <div>
          <h1>Extraction Report</h1>
          <p>Dynamic grouping, practical filters, cleaner flow.</p>
        </div>

        <div className="topbar-actions">
          {isAdminLikeUser && (
            <div className="combined-download-controls">
              <select
                value={downloadMonth}
                onChange={(event) => setDownloadMonth(Number(event.target.value))}
                disabled={isDownloadingCombinedReport}
                aria-label="Combined report month"
              >
                {MONTH_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>

              <select
                value={downloadYear}
                onChange={(event) => setDownloadYear(Number(event.target.value))}
                disabled={isDownloadingCombinedReport}
                aria-label="Combined report year"
              >
                {downloadYearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="download-btn"
                onClick={downloadCombinedExtractionReport}
                disabled={isDownloadingCombinedReport}
              >
                <FaDownload />
                {isDownloadingCombinedReport ? "Preparing..." : "Combined Excel"}
              </button>
            </div>
          )}

          <button type="button" className="ghost-btn" onClick={resetAllFilters}>
            <FaSyncAlt />
            Reset
          </button>

          <button
            type="button"
            className="primary-btn"
            onClick={() => setFilterPanelOpen((prev) => !prev)}
          >
            <FaFilter />
            Filters
            {totalSelectedFiltersCount > 0 && (
              <span className="filter-badge">{totalSelectedFiltersCount}</span>
            )}
          </button>
        </div>
      </div>

      <div className="hero-cards">
        <div className="hero-card">
          <span className="hero-label">Grouping</span>
          <strong>{currentGroupLabel}</strong>
          {groupBy === "actor" && groupPosition ? (
            <small>
              {
                actorPositions.find((item) => item.value === groupPosition)?.label
              }
            </small>
          ) : (
            <small>Current report bucket</small>
          )}
        </div>

        <div className="hero-card">
          <span className="hero-label">Metric</span>
          <strong>{metric === "value" ? "Value" : "Volume"}</strong>
          <small>{valueFormat === "crLac" ? "CR/LAC View" : "Normal View"}</small>
        </div>

        <div className="hero-card">
          <span className="hero-label">Date Range</span>
          <strong>{startDate}</strong>
          <small>to {endDate}</small>
        </div>
      </div>

      <div className="main-controls-card">
        <div className="main-controls-grid">
          <div className="control-block">
            <label>From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="control-block">
            <label>To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="control-block">
            <label>Group By</label>
            <select
              value={groupBy}
              onChange={(e) => {
                const next = e.target.value;
                setGroupBy(next);
                if (next !== "actor") {
                  setGroupPosition("");
                  setGroupActorSelection([]);
                }
              }}
            >
              {groupingOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          {groupBy === "actor" && (
            <div className="control-block">
              <label>Actor Position</label>
              <select
                value={groupPosition}
                onChange={(e) => {
                  setGroupPosition(e.target.value);
                  setGroupActorSelection([]);
                }}
              >
                <option value="">Select position</option>
                {visibleActorPositionItems.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="control-block">
            <label>Brand</label>
            <input
              type="text"
              placeholder="Optional"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
            />
          </div>

          <div className="control-block">
            <label>Segment</label>
            <input
              type="text"
              placeholder="Optional (e.g. 10-20)"
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
            />
          </div>
        </div>

        <div className="toggle-row">
          <div className="toggle-wrap">
            <TextToggle
              textFirst="default"
              textSecond="share"
              setText={setView}
              selectedText={view}
            />
            <TextToggle
              textFirst="crLac"
              textSecond="normal"
              setText={setValueFormat}
              selectedText={valueFormat}
            />
            <TextToggle
              textFirst="value"
              textSecond="volume"
              setText={setMetric}
              selectedText={metric}
            />
          </div>
        </div>
      </div>

      {groupBy === "actor" && groupPosition && (
        <div className="group-actor-card">
          <div className="group-actor-header">
            <div>
              <h3>Grouped Actor Context</h3>
              <p>
                Previewing actor list for <strong>{groupPosition.toUpperCase()}</strong>.
              </p>
            </div>
            <div className="mini-badge">
              <FaLayerGroup />
              {groupActorOptions.length} options
            </div>
          </div>

          <div className="group-actor-preview">
            {groupActorOptions.slice(0, 12).map((item) => (
              <div className="group-actor-pill" key={item.code}>
                {item.label}
              </div>
            ))}
            {groupActorOptions.length > 12 && (
              <div className="group-actor-pill more-pill">
                +{groupActorOptions.length - 12} more
              </div>
            )}
          </div>
        </div>
      )}

      <div className="active-filters-strip">
        <div className="strip-title">Active Filters</div>
        <div className="strip-content">
          {Object.keys(selectedActorFilters).map((type) => renderChips(type))}
          {Object.keys(selectedDealerFilters).map((type) => renderChips(type))}
          {totalSelectedFiltersCount === 0 && (
            <div className="empty-filter-text">No extra filters selected</div>
          )}
        </div>
      </div>

      {filterPanelOpen && (
        <div className="filter-panel-overlay">
          <div className="filter-panel" ref={panelRef}>
            <div className="filter-panel-header">
              <div>
                <h3>Advanced Filters</h3>
                <p>All selected filters work together with grouping.</p>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setFilterPanelOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="filter-panel-body">
              <div className="filter-sidebar">
                {visibleActorPositionItems.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    className={activeFilterTab === item.value ? "active" : ""}
                    onClick={() => {
                      setActiveFilterTab(item.value);
                      setSearchText("");
                    }}
                  >
                    {item.label}
                    {(selectedActorFilters[item.value] || []).length > 0 && (
                      <span>{(selectedActorFilters[item.value] || []).length}</span>
                    )}
                  </button>
                ))}

                {DEALER_FILTER_TYPES.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={activeFilterTab === item.key ? "active" : ""}
                    onClick={() => {
                      setActiveFilterTab(item.key);
                      setSearchText("");
                    }}
                  >
                    {item.label}
                    {(selectedDealerFilters[item.key] || []).length > 0 && (
                      <span>{(selectedDealerFilters[item.key] || []).length}</span>
                    )}
                  </button>
                ))}
              </div>

              <div className="filter-content">
                <div className="filter-content-top">
                  <div>
                    <h4>
                      {visibleActorPositionItems.find((p) => p.value === activeFilterTab)?.label ||
                        DEALER_FILTER_TYPES.find((d) => d.key === activeFilterTab)?.label ||
                        "Filters"}
                    </h4>
                    <small>Select one or more values</small>
                  </div>

                  <div className="filter-content-actions">
                    <input
                      type="text"
                      placeholder="Search name, code or value"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    <button type="button" className="ghost-btn" onClick={clearCurrentTab}>
                      Clear
                    </button>
                  </div>
                </div>

                {renderChips(activeFilterTab)}

                <div className="option-grid">
                  {filteredCurrentOptions.length > 0 ? (
                    filteredCurrentOptions.map((item) => {
                      const isSelected = currentTabSelected.some((selected) =>
                        isActorTab(activeFilterTab)
                          ? selected.code === item.code
                          : selected.value === item.value
                      );

                      return (
                        <button
                          type="button"
                          key={item.code || `${activeFilterTab}-${String(item.value)}`}
                          className={`option-pill ${isSelected ? "selected" : ""}`}
                          onClick={() => toggleSelection(activeFilterTab, item)}
                        >
                          <span>{item.label || item.name || item.value}</span>
                          {isActorTab(activeFilterTab) && item.code && (
                            <small>{item.code}</small>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <div className="empty-options">No options found</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="table-card">
        <div className="table-card-header">
          <div>
            <h3>Report Output</h3>
            <p>
              {groupBy === "price_segment"
                ? "Price bucket wise report"
                : "Dynamic grouped report"}
            </p>
          </div>
          <div className="table-summary">
            <span>{reportRows.length} rows</span>
          </div>
        </div>

        <div className="extraction-report-table">
          <table>
            <thead>
              <tr>
                {header.map((key) => (
                  <th key={key} className="heatmap-header">
                    {key}
                  </th>
                ))}
              </tr>
            </thead>

            {isLoading ? (
              <TableBodyLoading columnCount={header.length || 12} />
            ) : (
                <tbody>
                {reportRows.length > 0 ? (
                    reportRows.map((row, i) => {
                    const heatmapStats = getHeatmapStats(row, header);

                    const isTotalRow =
                        row["Price Class"] === "Total" || row["Group"] === "Total";

                    return (
                        <tr key={i} className={isTotalRow ? "total-row" : ""}>
                        {header.map((headerKey) => {
                            const value =
                            row[headerKey] !== undefined
                                ? row[headerKey]
                                : headerKey === "Total"
                                ? "0"
                                : "";

                            const isHeatmapColumn = ![
                            "Price Class",
                            "Group",
                            "Rank of Samsung",
                            "Total",
                            ].includes(headerKey);

                            const isNumeric = !isNaN(parseNumericValue(value));
                            const isProductCell =
                              isNumeric &&
                              parseNumericValue(value) > 0 &&
                              !["Price Class", "Group", "Rank of Samsung"].includes(headerKey);

                            const { background, text } =
                            isHeatmapColumn && isNumeric && !isTotalRow
                                ? getSmartHeatmapColor(value, heatmapStats)
                                : { background: "", text: "" };

                            return (
                            <td
                                key={headerKey}
                                className={isProductCell ? "product-drilldown-cell" : ""}
                                onClick={() => isProductCell && openProductDetail(row, headerKey)}
                                style={{
                                textAlign: "center",
                                ...(isHeatmapColumn && isNumeric && !isTotalRow
                                    ? {
                                        backgroundColor: background,
                                        color: text,
                                        fontWeight: "bold",
                                        padding: "8px 5px",
                                    }
                                    : {}),
                                }}
                            >
                                {isHeatmapColumn && isNumeric ? (
                                view === "share"
                                  ? value
                                  : valueFormat === "normal"
                                    ? formatNormalNumber(value)
                                    : formatCrLacNumber(value)
                                ) : headerKey === "Total" ? (
                                valueFormat === "normal"
                                  ? formatNormalNumber(value)
                                  : formatCrLacNumber(value)
                                ) : (
                                value
                                )}
                            </td>
                            );
                        })}
                        </tr>
                    );
                    })
                ) : (
                    <tr>
                    <td colSpan={header.length || 1} style={{ textAlign: "center" }}>
                        No data available
                    </td>
                    </tr>
                )}
                </tbody>
            )}
          </table>
        </div>
      </div>

      {productDetail.open && (
        <div className="extraction-product-modal-backdrop" onMouseDown={() => setProductDetail((v) => ({ ...v, open: false }))}>
          <div className="extraction-product-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="extraction-product-modal-header">
              <div>
                <h3>{productDetail.title}</h3>
                <p>Model-wise contribution for the selected table cell</p>
              </div>
              <button type="button" onClick={() => setProductDetail((v) => ({ ...v, open: false }))} aria-label="Close">
                <FaTimes />
              </button>
            </div>
            <div className="extraction-product-modal-body">
              {productDetail.loading ? (
                <div className="product-modal-state">Loading products...</div>
              ) : productDetail.error ? (
                <div className="product-modal-state error">{productDetail.error}</div>
              ) : productDetail.rows.length === 0 ? (
                <div className="product-modal-state">No product rows found</div>
              ) : (
                <table>
                  <thead>
                    <tr>{productDetail.columns.map((column) => <th key={column}>{column}</th>)}</tr>
                  </thead>
                  <tbody>
                    {productDetail.rows.map((detailRow, index) => (
                      <tr key={`${detailRow.Product || "product"}-${index}`}>
                        {productDetail.columns.map((column) => (
                          <td key={column}>
                            {column === "% of Cell"
                              ? `${Number(detailRow[column] || 0).toFixed(2)}%`
                              : column === "Value"
                                ? formatCrLacNumber(detailRow[column])
                                : column === "Volume"
                                  ? formatNormalNumber(detailRow[column])
                                  : detailRow[column] ?? "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      <DealerShopInsights
        startDate={startDate}
        endDate={endDate}
        dropdownValue={hierarchySelections}
        dealerFilters={selectedDealerFilters}
        scopedDealerCodes={scopedDealerCodes}
      />
    </div>
  );
}

const ACTOR_POSITION_KEYS = ["smd", "zsm", "asm", "mdd", "tse", "so", "dealer"];

export default DynamicExtractionReport;
